import type { Registry as Services } from '@ember/service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { Account, AccountListItem } from 'frontend-payments/models';

export default class AccountSwitcherService extends Service {
  @service declare errorTracking: Services['error-tracking'];
  @service declare store: Services['store'];

  @tracked availableAccounts: AccountListItem[] = [];
  @tracked currentAccountId: string | null = null;

  private readonly STORAGE_KEY = 'selected-account-id';

  loadAccountsTask = task(async () => {
    try {
      const response =
        await this.store.findAll<AccountListItem>('account-list-item');
      this.availableAccounts = response.slice();

      // Try to restore the saved account from localStorage
      const savedAccountId = this.restoreCurrentAccountFromStorage();

      // Check if the saved account exists in the available accounts
      const savedAccountExists =
        savedAccountId &&
        this.availableAccounts.some(
          (account) => account.accountNumber === savedAccountId,
        );

      if (savedAccountExists) {
        // Use the saved account if it exists
        this.currentAccountId = savedAccountId;
      } else if (!this.currentAccountId && this.availableAccounts.length > 0) {
        // Fall back to the first account if no saved account or saved account doesn't exist
        this.currentAccountId =
          this.availableAccounts[0]?.accountNumber || null;
        // Save the new selection to localStorage
        this.saveCurrentAccountToStorage();
      }

      return this.availableAccounts;
    } catch (error) {
      this.errorTracking.captureException(error);
      throw error;
    }
  });

  loadCurrentAccountTask = task(async (accountId: string) => {
    try {
      // Clear any existing cached account data
      const existingAccount = this.store.peekRecord('account', accountId);
      if (existingAccount) {
        this.store.unloadRecord(existingAccount);
      }

      return await this.store.findRecord<Account>('account', accountId, {
        reload: true,
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      throw error;
    }
  });

  get currentAccount(): AccountListItem | null {
    if (!this.currentAccountId) {
      return null;
    }
    return (
      this.availableAccounts.find(
        (account) => account.accountNumber === this.currentAccountId,
      ) || null
    );
  }

  get hasMultipleAccounts(): boolean {
    return this.availableAccounts.length > 1;
  }

  restoreCurrentAccountFromStorage(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (error) {
      // localStorage might not be available
      console.warn('Failed to restore account from localStorage:', error);
      return null;
    }
  }

  saveCurrentAccountToStorage(): void {
    try {
      if (this.currentAccountId) {
        localStorage.setItem(this.STORAGE_KEY, this.currentAccountId);
      } else {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      // localStorage might not be available or might be full
      console.warn('Failed to save account to localStorage:', error);
    }
  }

  switchAccount(accountId: string): void {
    this.currentAccountId = accountId;
    this.saveCurrentAccountToStorage();
  }
}

declare module '@ember/service' {
  interface Registry {
    'account-switcher': AccountSwitcherService;
  }
}
